import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import SectionEyebrow from "@/components/common/SectionEyebrow";
import StatusBadge from "@/components/common/StatusBadge";
import { publicApi } from "@/lib/api";
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Atom,
  Brain,
  Sparkles,
  Microscope,
  Globe2,
  Quote,
} from "lucide-react";

const PRINCIPLE_ICONS = [Globe2, Microscope, Sparkles, Atom, Brain, Quote];

export default function Home() {
  const [page, setPage] = useState(null);
  const [books, setBooks] = useState([]);
  const [members, setMembers] = useState([]);
  const [founder, setFounder] = useState(null);
  const [domains, setDomains] = useState([]);

  useEffect(() => {
    publicApi.page("home").then(setPage).catch(() => {});
    publicApi.books().then(setBooks).catch(() => {});
    publicApi.foundingMembers().then(setMembers).catch(() => {});
    publicApi.founder().then(setFounder).catch(() => {});
    publicApi.domains().then(setDomains).catch(() => {});
    publicApi.page("manifesto").then(() => {}).catch(() => {});
  }, []);

  const hero = page?.hero || {};
  const principles = [
    { title: "Unity of Knowledge", body: "Science and the wisdom traditions as complementary lenses on one reality." },
    { title: "Evidence + Experience", body: "Empirical evidence and lived experience honored as distinguishable, legitimate sources." },
    { title: "Open Inquiry", body: "We pursue questions without ideological allegiance — including those most avoid." },
    { title: "Interdisciplinarity", body: "Across disciplines by design — not as decoration but as method." },
    { title: "Ethical Awareness", body: "Every program judged by its consequences for humanity and the planet." },
    { title: "Humility & Wonder", body: "We hold our conclusions lightly. The mystery is larger than our methods." },
  ];

  return (
    <PublicLayout title="Home">
      {/* ============ HERO (dark cosmic) ============ */}
      <section
        data-testid="home-hero"
        className="relative bsca-dark bsca-cosmic bsca-grain overflow-hidden -mt-16 md:-mt-20 lg:-mt-[88px] pt-32 md:pt-40 pb-24 md:pb-32"
      >
        <div className="bsca-field-lines" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="max-w-3xl bsca-fade-up">
            <SectionEyebrow dark>{hero.eyebrow || "A Global Research & Consciousness Alliance"}</SectionEyebrow>
            <h1 className="font-serif text-white text-[2.5rem] leading-[1.05] sm:text-6xl lg:text-[5rem] tracking-tight">
              Expanding Science.
              <br />
              <span className="text-[#D4AF37]">Awakening Consciousness.</span>
            </h1>
            <p className="mt-7 text-white/75 text-lg max-w-2xl leading-relaxed">
              {hero.subheadline}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to={hero?.primary_cta?.href || "/engage#join"}
                data-testid="btn-hero-join"
                className="bsca-lift inline-flex items-center bg-[#D4AF37] text-[#050B14] font-medium px-7 py-3.5 rounded-sm hover:bg-[#F0D060]"
              >
                {hero?.primary_cta?.label || "Join the Alliance"} <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
              <Link
                to={hero?.secondary_cta?.href || "/beyond-science"}
                data-testid="btn-hero-explore"
                className="bsca-lift inline-flex items-center border border-white/30 text-white px-7 py-3.5 rounded-sm hover:bg-white/5 hover:border-[#D4AF37]/60"
              >
                {hero?.secondary_cta?.label || "Explore Beyond Science"}
              </Link>
            </div>
          </div>

          {/* signal counters */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-4xl">
            {[
              { k: "5", l: "Research Domains" },
              { k: "5", l: "Beyond Science Volumes" },
              { k: "9+", l: "Programs & Initiatives" },
              { k: "∞", l: "Frontier Questions" },
            ].map((s) => (
              <div key={s.l} className="bg-[#050B14] px-6 py-6">
                <div className="font-serif text-3xl text-[#D4AF37]">{s.k}</div>
                <div className="bsca-eyebrow text-white/60 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY WE EXIST + VISION (light) ============ */}
      <section data-testid="home-why" className="relative bg-[#F8F6F1] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <SectionEyebrow>Our Foundation</SectionEyebrow>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight">
              {page?.why_we_exist?.title || "Why We Exist"}
            </h2>
          </div>
          <div className="md:col-span-7 space-y-8">
            <p className="text-lg leading-relaxed text-[#1f2937]">
              {page?.why_we_exist?.body}
            </p>
            <div className="border-l-2 border-[#D4AF37] pl-6 py-2">
              <div className="bsca-eyebrow text-[#7A5A06]">{page?.vision?.title || "Our Vision"}</div>
              <p className="mt-2 text-base leading-relaxed text-[#4B5563]">{page?.vision?.body}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CORE PRINCIPLES (dark) ============ */}
      <section data-testid="home-principles" className="relative bsca-dark bsca-cosmic bsca-grain py-24 md:py-32 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="max-w-2xl">
            <SectionEyebrow dark>The Charter</SectionEyebrow>
            <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight">
              Six principles guide every <span className="text-[#D4AF37]">program, publication, and partnership.</span>
            </h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 bsca-stagger">
            {principles.map((p, i) => {
              const Icon = PRINCIPLE_ICONS[i % PRINCIPLE_ICONS.length];
              return (
                <div
                  key={p.title}
                  data-testid={`principle-${i}`}
                  className="group bg-[#050B14] p-8 md:p-10 hover:bg-[#0A1120] transition-colors"
                >
                  <Icon className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.4} />
                  <h3 className="mt-6 font-serif text-2xl text-white">{p.title}</h3>
                  <p className="mt-3 text-white/65 leading-relaxed text-[15px]">{p.body}</p>
                  <div className="mt-6 text-[10px] bsca-eyebrow text-white/30">0{i + 1}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-12">
            <Link to="/manifesto" data-testid="link-read-manifesto" className="inline-flex items-center text-[#D4AF37] bsca-link">
              Read the full Manifesto <ArrowUpRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ RESEARCH PREVIEW (light) ============ */}
      <section data-testid="home-research" className="relative bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div className="max-w-xl">
              <SectionEyebrow>Research & Frontier Questions</SectionEyebrow>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
                Five domains. One method of open inquiry.
              </h2>
            </div>
            <Link to="/research-compendium" data-testid="link-research-compendium" className="bsca-link text-sm font-medium inline-flex items-center">
              View the Research Compendium <ArrowUpRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-black/10 border border-black/10 bsca-stagger">
            {(domains.length ? domains : Array(5).fill({ title: "—", description: "" })).map((d, i) => (
              <Link
                key={d.slug || i}
                to={`/research-compendium${d.slug ? `?domain=${d.slug}` : ""}`}
                data-testid={`research-domain-${d.slug || i}`}
                className="group bg-white p-8 hover:bg-[#F8F6F1] transition-colors"
              >
                <div className="text-[10px] bsca-eyebrow text-[#7A5A06]">Domain 0{i + 1}</div>
                <h3 className="mt-4 font-serif text-xl leading-tight tracking-tight">{d.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#4B5563]">{d.description}</p>
                <div className="mt-6 inline-flex items-center text-xs text-[#050B14] group-hover:text-[#7A5A06]">
                  Explore <ArrowRight className="h-3 w-3 ml-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BEYOND SCIENCE BOOK SERIES (dark) ============ */}
      <section data-testid="home-books" className="relative bsca-dark bsca-cosmic bsca-grain py-24 md:py-32 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-14">
            <SectionEyebrow dark>The Beyond Science Series</SectionEyebrow>
            <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight">
              Five volumes. <span className="text-[#D4AF37]">One arc of inquiry.</span>
            </h2>
            <p className="mt-5 text-white/65 max-w-xl">
              From the architecture of the universe to the integration of science and spirit — a rigorous, public map of what we know, what we don{"'"}t, and why it matters.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {books.slice(0, 5).map((b) => (
              <Link
                to={`/beyond-science/${b.slug}`}
                key={b.slug}
                data-testid={`book-card-${b.slug}`}
                className="group bsca-lift block"
              >
                <div className="aspect-[3/4] bg-[#0A1120] border border-white/10 group-hover:border-[#D4AF37]/60 overflow-hidden relative">
                  <img
                    src={b.cover_image}
                    alt={b.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/30 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <StatusBadge status={b.status} label={b.status_label} />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="bsca-eyebrow text-[#D4AF37]/80 text-[10px]">Volume {b.volume}</div>
                    <h3 className="font-serif text-white text-lg leading-tight mt-1.5">{b.title}</h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/beyond-science" data-testid="link-all-books" className="inline-flex items-center text-[#D4AF37] bsca-link">
              View all five volumes <ArrowUpRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FOUNDER HIGHLIGHT (light) ============ */}
      <section data-testid="home-founder" className="relative bg-[#F8F6F1] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] relative overflow-hidden bg-[#0A1120]">
              {founder?.portrait_image && (
                <img src={founder.portrait_image} alt={founder.name} className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 ring-1 ring-inset ring-black/10" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                <div className="bsca-eyebrow text-[#D4AF37] text-[10px]">Founder</div>
                <div className="font-serif text-white text-xl">{founder?.name}</div>
                <div className="text-white/70 text-sm">{founder?.title}</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <SectionEyebrow>The Founder</SectionEyebrow>
            <Quote className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.3} />
            <blockquote className="font-serif text-3xl md:text-4xl tracking-tight leading-tight mt-4">
              {`"${founder?.quote || "When the known meets the unknowable, discovery becomes devotion."}"`}
            </blockquote>
            <p className="mt-8 text-[#4B5563] leading-relaxed">
              {founder?.short_bio}
            </p>
            <Link to="/founder" data-testid="link-founder" className="mt-8 inline-flex items-center bsca-link">
              Read the founder{"\u2019"}s profile <ArrowUpRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FOUNDING MEMBERS PREVIEW (light) ============ */}
      <section data-testid="home-members" className="relative bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div className="max-w-xl">
              <SectionEyebrow>Advisory Circle</SectionEyebrow>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Founding Members</h2>
              <p className="mt-4 text-[#4B5563] text-base">A globally distributed circle of scientists, technologists, and scholars.</p>
            </div>
            <Link to="/founding-members" data-testid="link-members" className="bsca-link text-sm font-medium inline-flex items-center">
              View governance <ArrowUpRight className="h-4 w-4 ml-1" />
            </Link>          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bsca-stagger">
            {members.map((m) => (
              <div key={m.slug} data-testid={`member-preview-${m.slug}`} className="group">
                <div className="aspect-[4/5] bg-[#F8F6F1] overflow-hidden relative">
                  {m.image && (
                    <img src={m.image} alt={m.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  )}
                </div>
                <div className="mt-4">
                  <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">{m.role_category}</div>
                  <div className="font-serif text-lg leading-tight mt-1.5">{m.name}</div>
                  <div className="text-xs text-[#4B5563] mt-1">{m.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ JOIN CTA BAND (dark) ============ */}
      <section data-testid="home-cta" className="relative bsca-dark bsca-cosmic bsca-grain py-24 md:py-32 overflow-hidden">
        <div className="bsca-field-lines" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <SectionEyebrow dark>Join the Movement</SectionEyebrow>
          <h2 className="font-serif text-4xl md:text-6xl text-white tracking-tight leading-[1.05]">
            The alliance is built <br className="hidden md:block" />
            <span className="text-[#D4AF37]">by its members.</span>
          </h2>
          <p className="mt-6 text-white/70 max-w-2xl mx-auto">
            Free and open globally. Research Fellows, Ambassadors, Institutional Partners, Student Circles, and serious readers welcome.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/engage#join" data-testid="btn-cta-join" className="bsca-lift inline-flex items-center bg-[#D4AF37] text-[#050B14] font-medium px-8 py-4 rounded-sm hover:bg-[#F0D060]">
              Become a Member <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
            <Link to="/engage#contribution" data-testid="btn-cta-contribute" className="bsca-lift inline-flex items-center border border-white/30 text-white px-8 py-4 rounded-sm hover:bg-white/5 hover:border-[#D4AF37]/60">
              Submit a Contribution
            </Link>
          </div>
        </div>
      </section>

      {/* ============ NEWSLETTER / CONTACT ============ */}
      <section data-testid="home-newsletter" className="relative bg-[#F8F6F1] py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <SectionEyebrow>Stay in the conversation</SectionEyebrow>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight">Receive BSCA{"'"}s research, talks, and book updates.</h2>
          <p className="mt-4 text-[#4B5563]">Occasional. Substantive. Never sold.</p>
          <NewsletterInline />
        </div>
      </section>
    </PublicLayout>
  );
}

function NewsletterInline() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = email.trim();
    if (!v) {
      setErr("Please enter your email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setErr("Please enter a valid email address.");
      return;
    }
    try {
      const { formsApi } = await import("@/lib/api");
      await formsApi.submit("newsletter", { email: v });
      setSent(true);
      setEmail("");
    } catch (e) {
      setErr("Couldn't subscribe — please try again in a moment.");
    }
  };

  if (sent) {
    return (
      <div data-testid="newsletter-success" className="mt-8 inline-flex items-center bsca-eyebrow text-[#7A5A06] border border-[#D4AF37]/40 px-5 py-3 rounded-full bg-[#FBF3D5]">
        Thank you — you{"'"}ll hear from BSCA soon.
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
      <input
        data-testid="newsletter-email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email"
        className="flex-1 bg-white border border-black/10 px-5 py-3.5 rounded-sm focus:outline-none focus:border-[#D4AF37]"
      />
      <button data-testid="newsletter-submit" type="submit" className="bsca-lift bg-[#050B14] text-white font-medium px-7 py-3.5 rounded-sm hover:bg-[#0A1120]">
        Subscribe
      </button>
      {err && <div className="text-red-700 text-sm sm:basis-full">{err}</div>}
    </form>
  );
}
